import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/apiBase';
import { api, getToken } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import AdminLoading from './AdminLoading';

const VARIATION_NAMES = ['Size', 'Color', 'Material'];

export default function AdminVariations() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState({ Size: [], Color: [], Material: [] });
  const [inputs, setInputs] = useState({ Size: '', Color: '', Material: '' });

  const loadVariations = React.useCallback(() => {
    setLoading(true);
    setError('');
    const token = getToken();
    const load = (path, useAuth = true) =>
      fetch(API_BASE + path, {
        headers: useAuth && token ? { Authorization: 'Bearer ' + token } : {}
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'Not found' : (r.statusText || 'Request failed')))));
    const apply = (res) => setData({ Size: res.Size || [], Color: res.Color || [], Material: res.Material || [] });
    load('/api/admin/variations')
      .then(apply)
      .catch((e) => {
        if (e.message === 'Not found') {
          load('/api/variations', false)
            .then(apply)
            .catch(() => setError('Backend not reachable. Start it with: node backend/server.js — then run: node backend/scripts/seed-variations.js'));
        } else {
          load('/api/variations', false)
            .then(apply)
            .catch(() => setError('Backend not reachable. Start it with: node backend/server.js — then run: node backend/scripts/seed-variations.js'));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadVariations();
  }, [loadVariations]);

  const addValue = (name, value) => {
    const v = (typeof value === 'string' ? value : '').trim();
    if (!v) return;
    const list = data[name] || [];
    if (list.includes(v)) return;
    setData((prev) => ({ ...prev, [name]: [...list, v] }));
    setInputs((prev) => ({ ...prev, [name]: '' }));
  };

  const removeValue = (name, index) => {
    setData((prev) => ({ ...prev, [name]: (prev[name] || []).filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      await api('/api/admin/variations', { method: 'PUT', body: JSON.stringify(data) });
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFromCatalog = async () => {
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      await api('/api/admin/variations/sync-from-catalog', { method: 'POST' });
      await loadVariations();
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Sync failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    if (
      !window.confirm(
        'Remove every Size, Color, and Material option from the global list?\n\nProduct rows are not deleted; only this shared option list is cleared. Continue?'
      )
    ) {
      return;
    }
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      const empty = { Size: [], Color: [], Material: [] };
      await api('/api/admin/variations', { method: 'PUT', body: JSON.stringify(empty) });
      setData(empty);
      setInputs({ Size: '', Color: '', Material: '' });
      setSuccess(true);
    } catch (e) {
      setError(e.message || 'Could not clear variations.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Card sx={{ overflow: 'visible' }}>
        <CardHeader
          title="Variations"
          subheader="Size, Color, and Material lists are rebuilt from your product catalog whenever you create or update a product, or after CSV / Google Sheets sync. Use Save to override manually (until the next product change). Sync from catalog pulls the latest from all products."
        />
        <CardContent sx={{ pt: 0, '&:last-child': { pb: 3 } }}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError('')}
              action={<Button color="inherit" size="small" onClick={() => { setError(''); loadVariations(); }}>Retry</Button>}
              sx={{ mb: 3 }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(false)} sx={{ mb: 3 }}>
              Variations saved.
            </Alert>
          )}

          {VARIATION_NAMES.map((name) => (
            <Card
              key={name}
              variant="outlined"
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: 'grey.50',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'text.primary' }}>
                {name}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  label={`Add ${name}`}
                  placeholder={name === 'Size' ? 'e.g. S, M, L' : name === 'Color' ? 'e.g. Blue, Red' : 'e.g. Cotton, Silk'}
                  value={inputs[name]}
                  onChange={(e) => setInputs((prev) => ({ ...prev, [name]: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addValue(name, inputs[name]))}
                  sx={{ minWidth: 220 }}
                  variant="outlined"
                />
                <Button variant="outlined" onClick={() => addValue(name, inputs[name])}>
                  Add
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Current values ({(data[name] || []).length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: 40 }}>
                {(data[name] || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    None yet. Add above and click Save variations.
                  </Typography>
                ) : (
                  (data[name] || []).map((v, i) => (
                    <Chip
                      key={`${name}-${i}`}
                      label={v}
                      size="medium"
                      onDelete={() => removeValue(name, i)}
                      sx={{ bgcolor: 'background.paper' }}
                    />
                  ))
                )}
              </Box>
            </Card>
          ))}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1 }}>
            <Button variant="contained" size="large" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save variations'}
            </Button>
            <Button variant="outlined" size="large" onClick={handleSyncFromCatalog} disabled={saving}>
              Sync from catalog
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={handleDeleteAll}
              disabled={saving}
            >
              Delete all variations
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
